import React, { useState, useEffect, useRef, RefObject, MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import { hideModal } from 'redux/actions/global';
import Spinner from 'components/widgets/Spinner/Spinner';
import Icon, { IconName } from 'components/core/Icon/Icon';
import Button, { ButtonVariants } from 'components/core/Button/Button';
import Highlight from 'components/widgets/CameraInput/CameraHighlight';
import { GA_CATEGORIES, GA_ACTIONS, CAMERA_CANVAS_DIMENSIONS } from 'consts';
import { useRect, useAnalytics, useCameraStream } from 'hooks';
import { fileToDataUri } from 'utilities/global';
import { ThemeFamilies } from 'models';

interface IProps {
  isSelfie: boolean;
  onCapture: (img: string) => void;
}

const CameraStream = ({ isSelfie, onCapture }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tracker = useAnalytics();

  const containerRef = useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement>;
  const videoRef = useRef<HTMLVideoElement>();
  const canvasRef = useRef<HTMLCanvasElement>();

  const { openCameraStream, closeCameraStream } = useCameraStream({ isSelfie, videoRef });

  const { width, height } = CAMERA_CANVAS_DIMENSIONS;
  const isLandscape = window.innerHeight < window.innerWidth;

  const [streamLoading, setStreamLoading] = useState(true);
  const [isFlashing, setIsFlashing] = useState(false);
  const [draft, setDraft] = useState(null);

  const { height: containerHeight } = useRect(containerRef);

  useEffect(() => {
    openCameraStream();
  }, []);

  const handleCanPlay = async () => {
    await videoRef?.current?.play();
    setStreamLoading(false);
  };

  const drawImage = (context: CanvasRenderingContext2D) =>
    context?.drawImage(
      videoRef.current as HTMLVideoElement, // source
      0, // sx
      0, // sy
      width, // sWidth
      height // sHeight
    );

  const convertToBlob = () =>
    canvasRef?.current?.toBlob(
      (blob: Blob | null) => {
        if (blob) {
          fileToDataUri(blob).then(dataUri => {
            setDraft(dataUri);
          });
        } else {
          setDraft(null);
        }
      },
      'image/jpeg',
      1
    );

  const handleCapture = () => {
    const context = canvasRef?.current?.getContext('2d');
    drawImage(context as CanvasRenderingContext2D);
    convertToBlob();
    setIsFlashing(true);
  };

  const clearRect = (context: CanvasRenderingContext2D) =>
    context?.clearRect(
      0,
      0,
      canvasRef?.current?.width || 0,
      canvasRef?.current?.height || 0
    );

  const handleClear = () => {
    const context = canvasRef?.current?.getContext('2d');
    clearRect(context as CanvasRenderingContext2D);
    setDraft(null);

    tracker.fireEvent({
      category: GA_CATEGORIES.CAMERA_PHOTO_SUBMISSION,
      action: `${GA_ACTIONS.CLICK} - ${GA_CATEGORIES.CAMERA_PHOTO_SUBMISSION} - Retake`
    });
  };

  const cancelCameraModal = () => {
    dispatch(hideModal());
    closeCameraStream();
    tracker.fireEvent({
      category: GA_CATEGORIES.CAMERA_PHOTO_SUBMISSION,
      action: `${GA_ACTIONS.CLICK} - ${GA_CATEGORIES.CAMERA_PHOTO_SUBMISSION} - Close`
    });
  };

  const capture = () => {
    closeCameraStream();
    onCapture(draft || '');
  };

  const videoProps = {
    ref: videoRef as RefObject<HTMLVideoElement>,
    onCanPlay: handleCanPlay,
    playsInline: true,
    muted: true
  };

  const canvasProps = {
    ref: canvasRef as RefObject<HTMLCanvasElement>,
    width,
    height
  };

  const renderCameraVideoContent = () => (
    <>
      <Video {...videoProps} />
      <HighlightWrapper>
        <Highlight
          oval={isSelfie}
          width={containerHeight}
          height={0}
          isLandscape={isLandscape}
        />
        <Canvas {...canvasProps} />
      </HighlightWrapper>
      {renderCtaButtons()}
    </>
  );

  const cancelButtonProps = {
    themeFamily: ThemeFamilies.PRIMARY,
    variant: ButtonVariants.TEXT,
    onClick: cancelCameraModal
  };

  const approveButtonProps = {
    onClick: capture,
    disabled: !draft,
    variant: ButtonVariants.FILLED
  };

  const renderCameraCtaButtons = () => (
    <>
      <CancelButton {...cancelButtonProps}>{t('cancel')}</CancelButton>
      <ApproveButton {...approveButtonProps}>{t('approve')}</ApproveButton>
    </>
  );

  const flashProps = {
    flash: isFlashing,
    onAnimationEnd: () => setIsFlashing(false)
  };

  const renderCameraCanvas = () => (
    <VideoWrapper>
      {streamLoading && (
        <StreamLoader>
          <Spinner isInner size={90} />
        </StreamLoader>
      )}
      {renderCameraVideoContent()}
      <Flash {...flashProps} />
    </VideoWrapper>
  );

  const renderCtaButtons = () => (
    <div>
      <DesktopCapture onClick={draft ? handleClear : handleCapture}>
        <StyledIcon name={IconName.CAMERA} />
      </DesktopCapture>
      <ButtonsWrapper>{renderCameraCtaButtons()}</ButtonsWrapper>
    </div>
  );

  const contentProps = {
    ref: containerRef as RefObject<HTMLDivElement>
  };

  const content = <StyledContent {...contentProps}>{renderCameraCanvas()}</StyledContent>;

  return content;
};

export default CameraStream;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
`;

const VideoWrapper = styled.div`
  display: flex;
  position: absolute;
  width: 83%;
  height: 69%;
`;

const Video = styled.video`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
`;

const flashAnimation = keyframes`
  from {
    opacity: 0.75;
  }

  to {
    opacity: 0;
  }
`;

const HighlightWrapper = styled.div`
  overflow: hidden;
  position: relative;
  height: 100%;
  width: 100%;
`;

const Canvas = styled.canvas`
  top: 50rem;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
`;

const Flash = styled.div<{ flash: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0;
  background-color: ${({ theme }) => theme.colors.white};

  ${({ flash }) => {
    if (flash) {
      return css`
        animation: ${flashAnimation} 750ms ease-out;
      `;
    }
  }}
`;

const DesktopCapture = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  top: ${({ theme }) => (theme.view.isSdk ? '38.5rem' : '100%')};
  transform: translate(-50%, -50%);
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 50%;
  z-index: 10;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.grey.default};
  border: 0.14rem solid ${({ theme }) => theme.colors.white};
`;

const ButtonsWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: ${({ theme }) => (theme.view.isSdk ? '2.5rem' : '5rem')};
`;

const ctaButton = css`
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  height: 4.4rem;
`;

const ApproveButton = styled(Button)`
  ${ctaButton};
`;

const CancelButton = styled(Button)`
  ${ctaButton};
  background-color: ${({ theme }) => theme.colors.white};

  &:hover {
    background: unset;
  }

  &:first-of-type {
    margin-right: 2.5rem;
  }
`;

const StreamLoader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.white};
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledIcon = styled(Icon)`
  font-size: 3.2rem;
  color: ${({ theme }) => theme.hcp.global.colors.primary};
`;
